"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import CountUp from "@/components/ui/CountUp";

export default function WhoWeAreSection() {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const els = ref?.current?.querySelectorAll(".reveal");
    if (!els) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            (e.target as HTMLElement).classList.add("visible");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="section-navy"
      style={{ padding: "120px 0" }}
      aria-labelledby="who-we-are-heading"
    >
      <div
        className="px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center"
        style={{ maxWidth: "1280px", margin: "0 auto" }}
      >
        {/* Image Column */}
        <div className="reveal img-offset-wrapper order-2 lg:order-1">
          <div
            className="overflow-hidden"
            style={{ borderRadius: "4px", aspectRatio: "4/5" }}
          >
            {/* Use next/image with explicit width/height and 100% sizing so it fills the wrapper
                without requiring the parent to be `position: relative`. */}
            <Image
              src="/assets/images/others/mineral_1.jpeg"
              alt="Open-pit mining operation with heavy equipment extracting ore"
              width={800}
              height={1000}
              style={{ objectFit: "cover", width: "100%", height: "100%" }}
              priority
            />
          </div>
        </div>

        {/* Text Column */}
        <div className="order-1 lg:order-2 flex flex-col gap-8">
          <div className="reveal">
            <span className="type-subheading">Who We Are</span>
          </div>

          <h2
            id="who-we-are-heading"
            className="type-display-dark reveal reveal-delay-1"
          >
            A dedicated counterparty
            <br />
            for refinery-grade antimony.
          </h2>

          <p
            className="type-body-lg reveal reveal-delay-2"
            style={{ color: "rgba(255,255,255,0.38)" }}
          >
            GFS Service Ltd. is a Thailand-based strategic commodity supplier
            with direct access to primary antimony production in Central Asia
            and Southern Africa. We serve lead-acid battery recyclers, flame-retardant
            compounders, and non-ferrous smelters requiring consistent, grade-certified
            feed.
          </p>

          <p
            className="type-body reveal reveal-delay-2"
            style={{ color: "rgba(255,255,255,0.38)" }}
          >
            Our commercial team structures offtake agreements from 500 to 5,000
            metric tonnes per annum, with quarterly or spot-top-up tranches.
            Every tonne ships with full documentation — CoA, CoC, export licence,
            and ESG disclosure.
          </p>

          {/* Credential pills */}
          <div className="reveal reveal-delay-3 flex flex-wrap gap-3">
            {[
              "ISO 9001:2015",
              "LBMA Responsible Sourcing",
              "OECD Due Diligence",
              "UK Export Control",
            ].map((badge) => (
              <span
                key={badge}
                style={{
                  fontFamily: "var(--font-sans)",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.38)",
                  background: "var(--color-navy-mid)",
                  border: "1px solid var(--color-navy-light)",
                  padding: "6px 14px",
                  borderRadius: "2px",
                }}
              >
                {badge}
              </span>
            ))}
          </div>

          {/* Stats row */}
          <div
            className="reveal reveal-delay-4 grid grid-cols-3 gap-6 pt-4"
            style={{ borderTop: "1px solid rgba(255, 255, 255, 0.1)" }}
          >
            {[
              { value: 2009, label: "Year Established" },
              { value: 27, label: "Countries Shipped To" },
              { value: 420, label: "Contracted Value", suffix: "M+" },
            ].map((s) => (
              <div key={s.label}>
                <p className="stat-number" style={{ color: "var(--color-white)" }}>
                  {typeof s.value === "number" ? (
                    <CountUp end={s.value} suffix={s.suffix} />
                  ) : (
                    s.value
                  )}
                </p>

                <p className="type-caption" style={{ marginTop: "4px" }}>
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}